import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, BlockStack } from "@shopify/polaris";
import DatePickerx from "features/datepicker/date-picker";
import { approveMedical } from "services/onboarding.service";
import { fetchOnboardingDetails } from "containers/onboarding-view-container/onboarding-container.slice";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";

interface ApproveMedicalModalProps {
  companyId: string;
  onboardingId: string;
  onApproved?: () => void;
}

/**
 * modalX content for approving a medical check (hire step_3).
 * Collects the validity period (valid from / valid until), then approves.
 */
const ApproveMedicalModal: React.FC<ApproveMedicalModalProps> = ({
  companyId,
  onboardingId,
  onApproved,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const [validFrom, setValidFrom] = useState<number | null>(null);
  const [validUntil, setValidUntil] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleApprove = async () => {
    if (!companyId || !onboardingId || !validFrom || !validUntil) return;
    if (validFrom > validUntil) {
      toast.error(t("medical_validity_invalid"));
      return;
    }
    setSubmitting(true);
    try {
      await approveMedical(companyId, onboardingId, { validFrom, validUntil });
      toast.success(t("medical_check_approved"));
      dispatch(fetchOnboardingDetails({ companyId, onboardingId }));
      onApproved?.();
      close();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.error || e?.response?.data?.message || t("error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      <BlockStack gap="400">
        <p className="text-sm text-gray-600">{t("enter_medical_validity")}</p>
        <DatePickerx
          title={t("medical_valid_from")}
          selected={validFrom ? new Date(validFrom).toISOString().split("T")[0] : ""}
          onChange={(d: Date | null) => setValidFrom(d ? d.getTime() : null)}
          isRequired
          disableDatesAfter={null}
          fullWidth
        />
        <DatePickerx
          title={t("medical_valid_until")}
          selected={
            validUntil ? new Date(validUntil).toISOString().split("T")[0] : ""
          }
          onChange={(d: Date | null) => setValidUntil(d ? d.getTime() : null)}
          isRequired
          disableDatesAfter={null}
          disableDatesBefore={(validFrom ? new Date(validFrom) : undefined) as any}
          fullWidth
        />
      </BlockStack>
      <div className="flex justify-end gap-2">
        <Button onClick={close}>{t("cancel")}</Button>
        <Button
          variant="primary"
          onClick={handleApprove}
          loading={submitting}
          disabled={!validFrom || !validUntil || submitting}
        >
          {t("approve")}
        </Button>
      </div>
    </div>
  );
};

export default ApproveMedicalModal;
